import React, { useCallback, useMemo, useState } from 'react';
import { ReactComponent as LinkIcon } from '../../../../../../../../assets/LinkIcon.svg';
import { ReactComponent as AddIcon } from '../../../../../../../../assets/Add.svg';
import { Popover } from '@round/ui-kit';
import OptionsList from '../../../../../../../../ui/DataEntry/SelectBox/components/OptionsList/OptionsList';
import useNonNullContext from '../../../../../../../../Hooks/useNonNullContext';
import { SongListContext } from '../../../../../../../Song/contexts/SongListContext';
import { SelectBoxOptionWithId } from '../../../../../../../../ui/DataEntry/SelectBox/SelectBox.types.';
import Option from '../../../../../../../../ui/DataEntry/SelectBox/components/Option/Option';
import { ExploreTiktokAudiosTableRow, ExploreTiktokAudiosTableRowV2 } from '../../../../../../TikTok.types';
import Card from '../../../../../../../../ui/Card/Card';
import styles from './LinkToSongCell.module.css';
import Skeleton from '../../../../../../../../ui/Skeleton/Skeleton';
import { showNotification } from '../../../../../../../../helpers';
import Button from '../../../../../../../../ui/Buttons/Button/Button';
import Image from '../../../../../../../../ui/Image/Image';
import cn from 'classnames';
import { linkTiktokAudioToSongs, Song } from '@round/api';
import CreateSongModal from '../../../../../../../Song/features/CreateSongModal/CreateSongModal';

type LinkToSongCellProps = {
    row: ExploreTiktokAudiosTableRow | ExploreTiktokAudiosTableRowV2 | undefined;
    loading: boolean;
    onAudioLinked: (audioId: number, song: Song) => void;
    readonly?: boolean;
};

const LinkToSongCell = ({ loading, onAudioLinked, row, readonly }: LinkToSongCellProps) => {
    const [createSongModalOpen, setCreateSongModalOpen] = useState(false);
    const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);
    const { songs, search, setSearch, songsCount, songsLoading, loadMoreSongs } = useNonNullContext(SongListContext);
    const [song] = row?.linkedSongs ?? [];

    const songOptions: SelectBoxOptionWithId[] = useMemo(
        () =>
            songs.map((s) => ({
                id: s.id,
                name: s.title,
                subtitle: s.artists.map((a) => a.name).join(', '),
                picture: s.image ?? '',
            })),
        [songs]
    );

    const linkAudioToSong = useCallback(
        async (audioId: number, song: Song) => {
            try {
                await linkTiktokAudioToSongs(audioId, [song.id]);
                onAudioLinked(audioId, song);
                showNotification('Audio linked', 'info');
            } catch {
                showNotification('Could not link audio to song', 'error');
            }
        },
        [onAudioLinked]
    );

    if (loading) {
        return <Skeleton />;
    }

    return (
        <div
            onClick={(e) => {
                if (!readonly) {
                    e.stopPropagation();
                }
            }}
        >
            <button ref={setButtonRef} className={cn(styles.button, { [styles.readonly]: readonly })}>
                {song ? (
                    <div className={styles.songContainer}>
                        <Image src={song.image ?? ''} className={styles.songImage} />
                        <div className={styles.songDetails}>
                            <p className={styles.songTitle}>{song.title}</p>
                            <p className={styles.songSubtitle}>{song.artists.map((a) => a.name).join(', ')}</p>
                        </div>
                    </div>
                ) : readonly ? (
                    <span>No song linked</span>
                ) : (
                    <>
                        <LinkIcon className={styles.buttonIcon} />
                        <span>Link audio to song</span>
                    </>
                )}
            </button>
            <Popover
                disabled={readonly}
                anchorElement={buttonRef}
                showOn="click"
                options={{ placement: 'bottom-end' }}
                onShowPopoverChange={(show) => {
                    if (!show) {
                        setSearch('');
                    }
                }}
            >
                {(setShow) => (
                    <Card className={styles.dropdown}>
                        <OptionsList
                            searchable
                            searchPlaceholder="Search song"
                            searchQuery={search}
                            setSearchQuery={setSearch}
                            loading={songsLoading}
                            loadMore={async () => loadMoreSongs()}
                            options={songOptions}
                            count={songsCount}
                        >
                            {({ item, loading, style }) => (
                                <Option
                                    item={item}
                                    style={style}
                                    loading={loading}
                                    onClick={(item, e) => {
                                        e.stopPropagation();
                                        const song = songs.find((s) => s.id === item?.id);
                                        if (!row || !song) {
                                            return;
                                        }

                                        linkAudioToSong(row.music_id, song);
                                        setShow(false);
                                    }}
                                />
                            )}
                        </OptionsList>
                        <Button
                            className={styles.createSongButton}
                            contentClassName={styles.createSongButtonContent}
                            onClick={() => {
                                setShow(false);
                                setCreateSongModalOpen(true);
                            }}
                        >
                            <AddIcon className={styles.createSongButtonIcon} />
                            Create song
                        </Button>
                    </Card>
                )}
            </Popover>

            <CreateSongModal
                isModalOpen={createSongModalOpen}
                closeModal={() => setCreateSongModalOpen(false)}
                onCreated={(song) => {
                    if (!row) {
                        return;
                    }
                    linkAudioToSong(row.music_id, {
                        ...song,
                        tiktok_audios: [row.music_id],
                        instagram_audios: 'instagram_audios' in song ? song.instagram_audios : [],
                    });
                }}
            />
        </div>
    );
};

export default LinkToSongCell;
